/**
 * ARB-256: progressively replace the PDP's labelled aggregate-inventory
 * fallback with Storefront API inventory in the active country context.
 *
 * This script owns the badge only. It must never enable or disable purchase
 * controls: server-rendered Liquid and the existing variant engines retain
 * that safety decision.
 */
(function () {
  'use strict';

  var CONFIG_SELECTOR = '[data-arbt-market-inventory-config]';
  var STOCK_SELECTOR = '[data-arb-stock-indicator]';
  var STATES = ['in', 'low', 'out', 'oversell'];
  var initialized = new WeakSet();

  var QUERY = [
    'query ArbtMarketInventory($id: ID!, $country: CountryCode!) @inContext(country: $country) {',
    '  product(id: $id) {',
    '    variants(first: 250) {',
    '      nodes { id availableForSale quantityAvailable }',
    '      pageInfo { hasNextPage }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function parseConfig(element) {
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  function stateFor(variant, threshold) {
    if (!variant.availableForSale) return 'out';
    if (typeof variant.quantityAvailable !== 'number') return 'in';
    if (variant.quantityAvailable <= 0) return 'oversell';
    if (variant.quantityAvailable <= threshold) return 'low';
    return 'in';
  }

  function labelFor(state, quantity, labels) {
    if (state === 'low') {
      return labels.low[String(quantity)] || labels.in;
    }
    return labels[state];
  }

  function applyBadge(stockElement, inventory) {
    if (!stockElement || !inventory) return;

    var labelElement = stockElement.querySelector('[data-arb-stock-label]');
    if (stockElement.getAttribute('data-state') !== inventory.state) {
      stockElement.setAttribute('data-state', inventory.state);
    }
    if (stockElement.getAttribute('data-inventory-scope') !== 'storefront-api-country-context') {
      stockElement.setAttribute('data-inventory-scope', 'storefront-api-country-context');
    }
    STATES.forEach(function (state) {
      stockElement.classList.toggle('arb-stock--' + state, state === inventory.state);
    });
    if (labelElement && labelElement.textContent !== inventory.label) {
      labelElement.textContent = inventory.label;
    }
  }

  function selectedVariantId(form) {
    if (!form) return '';
    var field = form.elements.namedItem('id');
    return field && field.value ? String(field.value) : '';
  }

  function initialize(configElement) {
    if (initialized.has(configElement)) return;
    initialized.add(configElement);

    var config = parseConfig(configElement);
    if (
      !config ||
      !config.endpoint ||
      !config.publicAccessToken ||
      !config.productId ||
      !config.country ||
      !config.formId
    ) {
      return;
    }

    var form = document.getElementById(config.formId);
    var sectionRoot = configElement.closest('.shopify-section') || document;
    var stockElement = sectionRoot.querySelector(STOCK_SELECTOR);
    if (!form || !stockElement) return;

    fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.publicAccessToken
      },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          id: config.productId,
          country: config.country
        }
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var country = payload && payload.extensions && payload.extensions.context
          ? payload.extensions.context.country
          : '';
        var connection = payload && payload.data && payload.data.product
          ? payload.data.product.variants
          : null;

        if (
          payload.errors ||
          country !== config.country ||
          !connection ||
          connection.pageInfo.hasNextPage ||
          connection.nodes.length !== config.variantCount
        ) {
          throw new Error('Incomplete or non-contextual Storefront inventory response');
        }

        var inventoryById = {};
        connection.nodes.forEach(function (variant) {
          var state = stateFor(variant, config.threshold);
          inventoryById[numericId(variant.id)] = {
            state: state,
            label: labelFor(state, variant.quantityAvailable, config.labels)
          };
        });

        function syncBadge() {
          applyBadge(stockElement, inventoryById[selectedVariantId(form)]);
        }

        function syncAfterVariantEngine() {
          window.setTimeout(syncBadge, 0);
        }

        sectionRoot.addEventListener('change', function (event) {
          if (event.target && event.target.name === 'id' && event.target.form === form) {
            syncAfterVariantEngine();
          }
        });
        form.addEventListener('variant:change', syncAfterVariantEngine);

        new MutationObserver(function (mutations) {
          var fallbackWasRestored = mutations.some(function (mutation) {
            return mutation.attributeName === 'data-inventory-scope' &&
              stockElement.getAttribute('data-inventory-scope') === 'global-inventory-fallback';
          });
          if (fallbackWasRestored) syncBadge();
        }).observe(stockElement, {
          attributes: true,
          attributeFilter: ['data-inventory-scope']
        });

        syncBadge();
      })
      .catch(function () {
        // The server-rendered global-inventory-fallback remains authoritative.
      });
  }

  function initializeAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CONFIG_SELECTOR).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    }, { once: true });
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
